import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import type { AnyNetworkElement, Kabelverteilschrank, TrafoStation } from '../../../../../types/network';
import type { LinkedAmqpOutletsResponse } from '../../../../../services/transformerAmqpService';
import { fetchMeasuredTrafoAt, type MeasuredTrafoAt } from '../../../../../services/elementHistoryService';
import { useNetworkElementsStore } from '../../../../../store/networkElementsStore';
import { useNetworkUIStore } from '../../../../../store/networkUIStore';
import { useSettingsStore } from '../../../../../store/settingsStore';
import { useCouplingStore } from '../../../../../store/couplingStore';
import { useZoneStore } from '../../../../../store/zoneStore';
import { useHistoricalPlaybackStore } from '../../../../../store/historicalPlaybackStore';
import { puToVolts } from '../../../../../utils/electricalConstants';
import { parseUtcTimestamp, formatInstantLabel as timeLabel } from '../../../../../utils/timeUtils';
import { shortElementName } from '../../../../../utils/elementDisplayName';
import {
  buildKvsSections,
  buildStationSection,
  ratedLvCurrentA,
  trafoNameplate,
  trafoVoltageLevels,
  type BusbarSwitchModel,
  type StationSection,
} from '../../../../../utils/stationDiagramModel';
import { fmt } from '../../../../../utils/numberFormat';
import { StationDiagram, type StationSpine } from './StationDiagram';

/**
 * Stationsinnenansicht / Schrankansicht — the Übersichtsschaltplan of a
 * transformer station or a Kabelverteilerschrank, opened from its panel.
 * Data: the cables at the busbar(s) and their fuses (model), the powerflow
 * frame held in the store (live poll or the historical playback instant),
 * for a station the measured trafo record at that instant and the
 * Lemonbeat/Station-MQTT outlets (live only), plus the Trennstellen switched
 * in the element. Clicking a feeder or its destination selects that element
 * on the map (the panel then shows it, so the view closes).
 */

interface Props {
  isOpen: boolean;
  element: TrafoStation | Kabelverteilschrank;
  /** Linked outlets already loaded by the trafo panel — null without a device. */
  lemonbeatData?: LinkedAmqpOutletsResponse | null;
  onClose: () => void;
}

const LegendFuse: React.FC<{ pulled?: boolean }> = ({ pulled }) => (
  <svg width="16" height="22" viewBox="0 0 16 22" className="inline-block align-middle text-gray-700 dark:text-primary-200">
    {pulled ? (
      <g transform="rotate(-38 8 4)">
        <rect x="3" y="4" width="10" height="16" rx="1.5" fill="none" stroke="currentColor" strokeDasharray="2 1.5" />
      </g>
    ) : (
      <>
        <line x1="8" y1="0" x2="8" y2="22" stroke="currentColor" strokeWidth="1.2" />
        <rect x="3" y="4" width="10" height="14" rx="1.5" fill="currentColor" className="text-green-500" stroke="currentColor" />
      </>
    )}
  </svg>
);

const EMPTY_SWITCHES: BusbarSwitchModel[] = [];

export const StationDiagramModal: React.FC<Props> = ({ isOpen, element, lemonbeatData = null, onClose }) => {
  const isTrafo = element.type === 'trafostation';
  const elements = useNetworkElementsStore(s => s.elements);
  const segments = useNetworkElementsStore(s => s.segments);
  const powerMeasurement = useNetworkElementsStore(s => s.powerMeasurements[element.id]);
  const couplings = useCouplingStore(s => s.couplings);
  const couplingFlows = useCouplingStore(s => s.flows);
  const selectCoupling = useCouplingStore(s => s.selectCoupling);
  const activeZoneId = useZoneStore(s => s.activeZoneId);
  const isHistorical = useHistoricalPlaybackStore(s => s.mode) === 'historical';
  const effectiveTs = useHistoricalPlaybackStore(s => s.effectiveTs);
  const cableLevels = useSettingsStore(s => s.thresholds.cableLoadLevels);
  const trafoLevels = useSettingsStore(s => s.thresholds.transformerLoadLevels);
  const setSelectedElement = useNetworkUIStore(s => s.setSelectedElement);
  const setHighlightedElement = useNetworkUIStore(s => s.setHighlightedElement);
  const setMapView = useNetworkUIStore(s => s.setMapView);

  // Fit-to-width only while it stays readable; wide stations open at 1:1 with horizontal scroll.
  const [fitChoice, setFitChoice] = useState<boolean | null>(null);
  const [measured, setMeasured] = useState<MeasuredTrafoAt | null>(null);

  // Measured trafo record at the shown instant (same source as the panel's Elektrische Größen).
  useEffect(() => {
    if (!isOpen || !isTrafo) return;
    let cancelled = false;
    const ts = isHistorical ? effectiveTs : new Date().toISOString();
    if (!ts) {
      setMeasured(null);
      return;
    }
    fetchMeasuredTrafoAt(element.id, ts)
      .then(rec => { if (!cancelled) setMeasured(rec); })
      .catch(() => { if (!cancelled) setMeasured(null); });
    return () => { cancelled = true; };
  }, [isOpen, isTrafo, element.id, isHistorical, effectiveTs]);

  useEffect(() => {
    if (!isOpen) return;
    const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose(); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [isOpen, onClose]);

  const model = useMemo<{ sections: StationSection[]; switches: BusbarSwitchModel[] }>(() => {
    if (element.type === 'trafostation') {
      return {
        sections: [buildStationSection({
          trafo: element,
          elements,
          segments,
          outlets: isHistorical ? null : lemonbeatData,
          couplings,
          couplingFlows,
          activeZoneId,
        })],
        switches: EMPTY_SWITCHES,
      };
    }
    return buildKvsSections({ kvs: element, elements, segments, couplings, couplingFlows, activeZoneId });
  }, [element, elements, segments, isHistorical, lemonbeatData, couplings, couplingFlows, activeZoneId]);

  const spine = useMemo<StationSpine | null>(() => {
    if (element.type !== 'trafostation') return null;
    const trafo = element;
    const pm = powerMeasurement;
    const ratingKva = lemonbeatData?.ratingPowerKva
      ?? (trafo.capacity > 10_000 ? trafo.capacity / 1000 : trafo.capacity) ?? null;
    const calcU = pm ? [pm.voltageL1Pu, pm.voltageL2Pu, pm.voltageL3Pu].map(v => (v != null ? puToVolts(v) : null)) : [null, null, null];
    const calcI = pm ? [pm.currentL1A ?? null, pm.currentL2A ?? null, pm.currentL3A ?? null] : [null, null, null];
    const measU = measured ? [measured.u1V, measured.u2V, measured.u3V] : [null, null, null];
    const measI = measured ? [measured.i1A, measured.i2A, measured.i3A] : [null, null, null];
    const hasMeasU = measU.some(v => v != null);
    const hasMeasI = measI.some(v => v != null);
    const hasCalcU = calcU.some(v => v != null);
    const hasCalcI = calcI.some(v => v != null);
    const s = pm?.totalApparentPowerKva ?? null;
    const p = pm?.totalActivePower ?? null;
    const calcCosPhi = s && s > 0 && p != null ? Math.min(1, Math.abs(p) / s) : null;
    const levels = trafoVoltageLevels(trafo);
    return {
      designator: 'T1',
      name: shortElementName(trafo) ?? trafo.name,
      hvKv: levels.hvKv,
      lvKv: levels.lvKv,
      ratingKva: ratingKva && ratingKva > 0 ? ratingKva : null,
      ratedCurrentA: ratedLvCurrentA(ratingKva),
      nameplate: trafoNameplate(trafo),
      loadingPct: pm?.loadingPercent ?? null,
      activePowerKw: p,
      reactivePowerKvar: pm?.totalReactivePowerKvar ?? null,
      apparentPowerKva: s,
      inService: trafo.inService !== false,
      voltageV: hasMeasU ? measU : calcU,
      voltageSource: hasMeasU ? 'measured' : hasCalcU ? 'calculated' : null,
      currentA: hasMeasI ? measI : calcI,
      currentSource: hasMeasI ? 'measured' : hasCalcI ? 'calculated' : null,
      frequencyHz: measured?.frequencyHz ?? null,
      cosPhi: measured?.cosPhi != null ? Math.abs(measured.cosPhi) : calcCosPhi,
      cosPhiSource: measured?.cosPhi != null ? 'measured' : calcCosPhi != null ? 'calculated' : null,
      measuredAtLabel: measured ? timeLabel(parseUtcTimestamp(measured.measuredAt)) : null,
      calcAtLabel: pm?.timestamp ? timeLabel(new Date(pm.timestamp)) : null,
    };
  }, [element, powerMeasurement, measured, lemonbeatData]);

  const handleSelectElement = useCallback((el: AnyNetworkElement) => {
    setSelectedElement(el);
    setHighlightedElement(el.id);
    if (el.type !== 'abgangsleitung' && el.coordinates && Number.isFinite(el.coordinates.lat) && Number.isFinite(el.coordinates.lng)) {
      setMapView(el.coordinates, 20);
    }
    onClose();
  }, [setSelectedElement, setHighlightedElement, setMapView, onClose]);

  const handleSelectCoupling = useCallback((id: string) => {
    selectCoupling(id);
    onClose();
  }, [selectCoupling, onClose]);

  if (!isOpen) return null;

  const fields = model.sections.flatMap(s => s.fields);
  const fitWidth = fitChoice ?? fields.length <= 8;
  const cableCount = fields.filter(f => f.kind === 'cable').length;
  const couplingCount = fields.filter(f => f.kind === 'coupling').length;
  const outletCount = fields.filter(f => f.kind === 'outlet').length;
  const standLabel = isHistorical
    ? `Archivstand ${effectiveTs ? timeLabel(parseUtcTimestamp(effectiveTs)) ?? '' : ''}`
    : 'Live';
  const name = shortElementName(element) ?? element.name;
  const title = isTrafo ? `Stationsansicht · ${name}` : `Schrankansicht · ${name}`;
  const kindLabel = isTrafo ? 'Übersichtsschaltplan 0,4-kV-Anlage' : 'Übersichtsschaltplan Kabelverteilerschrank';
  const sectionCount = model.sections.length;
  const headerTone = isTrafo
    ? { bg: 'from-orange-500/10 dark:from-orange-500/20', box: 'bg-orange-500/20 dark:bg-orange-500/30', icon: 'text-orange-600 dark:text-orange-400' }
    : { bg: 'from-cyan-500/10 dark:from-cyan-500/20', box: 'bg-cyan-500/20 dark:bg-cyan-500/30', icon: 'text-cyan-600 dark:text-cyan-400' };

  return createPortal(
    <div className="fixed inset-0 bg-black/50 dark:bg-black/70 flex items-center justify-center z-9999" onClick={onClose}>
      <div
        className="bg-white dark:bg-primary-800 rounded-xl shadow-2xl w-[96vw] max-w-[1800px] max-h-[92vh] overflow-hidden flex flex-col"
        role="dialog"
        aria-labelledby="station-diagram-title"
        onClick={e => e.stopPropagation()}
      >
        {/* Header */}
        <div className={`px-6 py-4 bg-linear-to-r ${headerTone.bg} to-white dark:to-primary-800 border-b border-gray-300 dark:border-primary-600 flex items-center justify-between gap-4`}>
          <div className="flex items-center space-x-3 min-w-0">
            <div className={`p-2 ${headerTone.box} rounded-lg shrink-0`}>
              <svg className={`w-5 h-5 ${headerTone.icon}`} fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 5h16M12 5v6m-6 0h12M6 11v8m6-8v8m6-8v8" />
              </svg>
            </div>
            <div className="min-w-0">
              <h3 id="station-diagram-title" className="text-lg font-semibold text-gray-900 dark:text-primary-50 truncate">
                {title}
              </h3>
              <p className="text-xs text-gray-500 dark:text-primary-400">
                {kindLabel}
                {!isTrafo ? ` · ${sectionCount} Sammelschiene${sectionCount !== 1 ? 'n' : ''}` : ''}
                {model.switches.length > 0 ? ` · ${model.switches.length} Kupplung${model.switches.length > 1 ? 'en' : ''}` : ''}
                {` · ${cableCount} Abgänge`}
                {couplingCount > 0 ? ` · ${couplingCount} Trennstelle${couplingCount > 1 ? 'n' : ''}` : ''}
                {outletCount > 0 ? ` · ${outletCount} Messstelle${outletCount > 1 ? 'n' : ''} ohne Kabel` : ''}
                {' · '}
                <span className={isHistorical ? 'text-orange-700 dark:text-orange-300 font-medium' : 'text-success-700 dark:text-success-300 font-medium'}>{standLabel}</span>
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2 shrink-0">
            <div className="inline-flex rounded-lg border border-gray-200 dark:border-primary-600 overflow-hidden text-xs">
              <button
                onClick={() => setFitChoice(true)}
                className={`px-3 py-1.5 ${fitWidth ? 'bg-info-500 text-white' : 'bg-white dark:bg-primary-700 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
                title="Ganze Anlage in die Breite einpassen"
              >
                Einpassen
              </button>
              <button
                onClick={() => setFitChoice(false)}
                className={`px-3 py-1.5 ${!fitWidth ? 'bg-info-500 text-white' : 'bg-white dark:bg-primary-700 text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-600'}`}
                title="Originalgröße, horizontal scrollen"
              >
                1:1
              </button>
            </div>
            <button
              onClick={onClose}
              className="p-2 text-gray-500 hover:text-gray-700 dark:text-primary-400 dark:hover:text-primary-200 rounded-lg hover:bg-gray-100 dark:hover:bg-primary-700"
              aria-label="Schließen"
            >
              <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        {/* Drawing */}
        <div className="flex-1 overflow-auto bg-white dark:bg-primary-800 px-4 py-4">
          <StationDiagram
            spine={spine}
            sections={model.sections}
            switches={model.switches}
            cableLevels={cableLevels}
            trafoLevels={trafoLevels}
            fitWidth={fitWidth}
            ariaLabel={title}
            onSelectElement={handleSelectElement}
            onSelectCoupling={handleSelectCoupling}
          />
        </div>

        {/* Legend / footer */}
        <div className="px-6 py-3 bg-gray-50 dark:bg-primary-700 border-t border-gray-300 dark:border-primary-600 flex flex-wrap items-center gap-x-5 gap-y-2 text-[11px] text-gray-600 dark:text-primary-200">
          <span className="inline-flex items-center gap-1.5"><LegendFuse /> Sicherung eingesetzt</span>
          <span className="inline-flex items-center gap-1.5"><LegendFuse pulled /> gezogen</span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-gray-400" /> &lt; {fmt(cableLevels.green, 0)} %
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-green-500 ml-2" /> ab {fmt(cableLevels.green, 0)} %
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-yellow-400 ml-2" /> ab {fmt(cableLevels.yellow, 0)} %
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-orange-500 ml-2" /> ab {fmt(cableLevels.orange, 0)} %
            <span className="inline-block w-2.5 h-2.5 rounded-sm bg-red-500 ml-2" /> ab {fmt(cableLevels.red, 0)} %
            <span className="ml-1 text-gray-400 dark:text-primary-400">(Auslastung Kabel / Sicherung{isTrafo ? ', Trafo nach Trafo-Stufen' : ''})</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="inline-block w-2 h-2 rounded-full bg-teal-500" /> gemessen
            <span className="inline-block w-2 h-2 rounded-full bg-blue-400 ml-2" /> berechnet (Lastfluss)
          </span>
          <span className="text-gray-400 dark:text-primary-400">
            Pfeil nach unten = Bezug aus der Sammelschiene, ↑ = speist die Sammelschiene · Klick auf Sicherung, Kabel oder Ziel wählt das Element auf der Karte
          </span>
          <button
            onClick={onClose}
            className="ml-auto px-4 py-1.5 bg-white dark:bg-primary-600 border border-gray-300 dark:border-primary-500 rounded-lg text-sm font-medium text-gray-700 dark:text-primary-200 hover:bg-gray-50 dark:hover:bg-primary-500 transition-colors"
          >
            Schließen
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
};
